/**
 * The admin panel's state.
 *
 * One store for a screen with two views in it — a list and a detail — because
 * they share everything that matters: whether the panel is reachable at all,
 * what went wrong last, and whether a request is in flight. Splitting them
 * would mean two copies of the `forbidden` handling, which is the one piece of
 * state on this screen it would be bad to get wrong twice.
 *
 * ## `access` is what the screen branches on
 *
 * Every route answers the same four ways, so the store collapses them into one
 * field the screen reads once:
 *
 * - `unknown` — nothing has been asked yet.
 * - `ok` — the last request was answered.
 * - `forbidden` — signed in, not an administrator. The panel shows a screen
 *   saying so. This is a *server* answer; the client never decides it.
 * - `signed-out` — no session, or it expired while the panel was open.
 * - `unavailable` — no accounts on this deployment, or none reachable.
 *
 * The three that are not `ok` are all reached by being *told*, never by
 * inferring: a screen that decided for itself that somebody was not an admin
 * would be a screen that could be wrong in the dangerous direction.
 *
 * ## The temporary password
 *
 * Held here for exactly as long as the detail view is open on that player, and
 * dropped on every navigation. It is the only secret this client ever holds
 * and there is no route that would hand it back a second time, so the store
 * treats losing it as the correct behaviour rather than as data loss.
 */
import type { AccountOutcome } from './account-io.js';
import {
  deletePlayer as deletePlayerRequest,
  fetchOverview,
  fetchPlayer,
  fetchPlayers,
  resetPassword as resetPasswordRequest,
} from './admin-io.js';
import type { AdminOverview, AdminPlayerDetail, AdminPlayerPage } from './admin-io.js';
import { RequestSequence } from './request-sequence.js';

export type AdminAccess = 'unknown' | 'ok' | 'forbidden' | 'signed-out' | 'unavailable';

interface AdminState {
  access: AdminAccess;
  overview: AdminOverview | null;
  page: AdminPlayerPage | null;
  /** The player the detail view is open on, or `null` for the list. */
  detail: AdminPlayerDetail | null;
  /** The search box's current text. The list is refetched from it. */
  query: string;
  /** One-based, as a person reads it. */
  pageNumber: number;
  busy: boolean;
  /** A refusal worth showing above the content. Cleared by the next attempt. */
  error: string;
  /** The last temporary password issued, shown once. */
  temporaryPassword: string | null;
  /** A sentence confirming something that just happened, e.g. a deletion. */
  notice: string;
}

export const admin: AdminState = $state({
  access: 'unknown',
  overview: null,
  page: null,
  detail: null,
  query: '',
  pageNumber: 1,
  busy: false,
  error: '',
  temporaryPassword: null,
  notice: '',
});

/**
 * One sequence for the list (and the overview that arrives with it) and one
 * for the detail view — see `request-sequence.ts`. A search box that is typed
 * into fires a request per pause, and they do not come back in order; without
 * these, the answer for "al" can land after the answer for "alice" and stay.
 * A stale response touches nothing: not the data, not `busy`, not `error`.
 */
const listRequests = new RequestSequence();
const detailRequests = new RequestSequence();

/**
 * Open the panel: the overview and the first page of the list, together.
 *
 * Together rather than in sequence because they are one screen and two round
 * trips in parallel is one wait. If either says `forbidden`, `apply` settles
 * `access` on it and the screen never renders the content at all.
 *
 * **This function reads `admin.query` and `admin.pageNumber` synchronously**,
 * before its first `await`, which makes it a reactive dependency of whatever
 * calls it. `AdminScreen.svelte`'s mount effect therefore wraps the call in
 * `untrack` — without it, typing in the search box re-runs that effect, whose
 * cleanup resets the query that was just typed. The comment there has the full
 * story; this note exists so the coupling is visible from both ends.
 */
export async function openPanel(): Promise<void> {
  const ticket = listRequests.begin();
  admin.busy = true;
  admin.error = '';
  const [overview, page] = await Promise.all([
    fetchOverview(),
    fetchPlayers(admin.query, admin.pageNumber),
  ]);
  if (!listRequests.isCurrent(ticket)) return;
  admin.busy = false;
  if (!apply(overview) || !apply(page)) return;
  admin.overview = overview.value;
  admin.page = page.value;
  admin.access = 'ok';
}

/** Refetch the list for the current query and page. */
export async function loadPlayers(): Promise<void> {
  const ticket = listRequests.begin();
  admin.busy = true;
  admin.error = '';
  const result = await fetchPlayers(admin.query, admin.pageNumber);
  // A newer list request owns the store now, `busy` included.
  if (!listRequests.isCurrent(ticket)) return;
  admin.busy = false;
  if (!apply(result)) return;
  admin.page = result.value;
  admin.access = 'ok';
}

export function search(query: string): Promise<void> {
  admin.query = query;
  // A new search is a new list, so it starts at its own beginning. Staying on
  // page four of a list that no longer has one is how an empty screen with no
  // explanation happens.
  admin.pageNumber = 1;
  return loadPlayers();
}

export function goToPage(pageNumber: number): Promise<void> {
  admin.pageNumber = Math.max(1, pageNumber);
  return loadPlayers();
}

export async function openPlayer(id: string): Promise<void> {
  admin.busy = true;
  admin.error = '';
  // A password belongs to the view it was issued in, and this is a new one.
  admin.temporaryPassword = null;
  admin.notice = '';
  const ticket = detailRequests.begin();
  const result = await fetchPlayer(id);
  // Somebody opened a different player, or went back to the list, while this
  // was in flight: the detail view is no longer this request's to fill.
  if (!detailRequests.isCurrent(ticket)) return;
  admin.busy = false;
  if (!apply(result)) return;
  admin.detail = result.value;
  admin.access = 'ok';
}

/** Back to the list, dropping everything the detail view was holding. */
export function closePlayer(): void {
  // A detail request still in flight is for a view that has just been closed.
  detailRequests.invalidate();
  admin.busy = false;
  admin.detail = null;
  admin.temporaryPassword = null;
  admin.error = '';
}

/**
 * Delete the player the detail view is open on.
 *
 * Returns whether it happened, so the screen can decide what to do next
 * rather than having that decided for it here. On success the list is
 * refetched — the totals on the overview and the row that is now gone are both
 * stale the instant this returns.
 */
export async function deletePlayer(id: string): Promise<boolean> {
  admin.busy = true;
  admin.error = '';
  try {
    const result = await deletePlayerRequest(id);
    if (!apply(result)) return false;
    const username = admin.detail?.player.username ?? 'That account';
    admin.detail = null;
    admin.temporaryPassword = null;
    admin.notice = `${username} has been deleted. The matches they played are kept, with their name and no account behind it.`;
    await Promise.all([refreshOverview(), loadPlayers()]);
    return true;
  } finally {
    admin.busy = false;
  }
}

/**
 * Issue a temporary password for a player.
 *
 * The plaintext lands in `admin.temporaryPassword` and nowhere else — not in
 * `localStorage`, not in the URL, and not in a second request that could fetch
 * it again. There is no such request, on purpose.
 */
export async function resetPassword(id: string): Promise<boolean> {
  admin.busy = true;
  admin.error = '';
  admin.temporaryPassword = null;
  try {
    const result = await resetPasswordRequest(id);
    if (!apply(result)) return false;
    // The password belongs to the view it was asked from. If that view was
    // closed, or moved to another player, while the request was in flight,
    // there is nowhere it may be shown.
    if (admin.detail?.player.id !== id) return false;
    admin.temporaryPassword = result.value.password;
    // Their sessions are gone, so "last seen" and everything else on this
    // player has moved. Re-read rather than patch.
    await openPlayerQuietly(id);
    return true;
  } finally {
    admin.busy = false;
  }
}

/** Drop everything. Called when the panel is left, so a later visit re-reads. */
export function resetPanel(): void {
  // Anything still in flight belongs to a panel that has been left.
  listRequests.invalidate();
  detailRequests.invalidate();
  admin.busy = false;
  admin.access = 'unknown';
  admin.overview = null;
  admin.page = null;
  admin.detail = null;
  admin.query = '';
  admin.pageNumber = 1;
  admin.error = '';
  admin.temporaryPassword = null;
  admin.notice = '';
}

async function refreshOverview(): Promise<void> {
  const result = await fetchOverview();
  if (result.status === 'ok') admin.overview = result.value;
}

/** Re-read the detail without clearing the password just issued. */
async function openPlayerQuietly(id: string): Promise<void> {
  const ticket = detailRequests.begin();
  const result = await fetchPlayer(id);
  if (!detailRequests.isCurrent(ticket)) return;
  if (result.status === 'ok') admin.detail = result.value;
}

/**
 * Fold one outcome into the store, and say whether the caller may carry on.
 *
 * The three refusals set `access`, because they are all "this panel is not
 * available to you right now" and the screen answers each with a whole view.
 * `refused` and `offline` set `error` instead and leave `access` alone: the
 * panel is still yours, that one request did not work.
 */
function apply<T>(result: AccountOutcome<T>): result is { status: 'ok'; value: T } {
  switch (result.status) {
    case 'ok':
      return true;
    case 'forbidden':
      admin.access = 'forbidden';
      return false;
    case 'signed-out':
      admin.access = 'signed-out';
      return false;
    case 'unavailable':
      admin.access = 'unavailable';
      return false;
    case 'refused':
      admin.error = result.message;
      return false;
    default:
      admin.error = 'Could not reach the server. Check the connection and try again.';
      return false;
  }
}
